import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowUpCircle, ThumbsUp, ThumbsDown, Bot, Check } from 'lucide-react';
import ApiService from '../services/ApiService';

function VirtualChatView({ relative, onBack }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [conversationUuid, setConversationUuid] = useState(null);
  const [needsMoreVideos, setNeedsMoreVideos] = useState(false);
  const scrollRef = useRef(null);

  const profileImageUrl = relative.profile?.profilePictureUrl || relative.profilePictureUrl || null;

  useEffect(() => {
    checkReadiness();
  }, [relative]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isThinking]);

  const checkReadiness = async () => {
    try {
      const result = await ApiService.checkChatReadiness(relative.uuid);
      if (result.readiness === 'insufficient') {
        setNeedsMoreVideos(true);
        setMessages([{
          id: 'readiness-prompt',
          role: 'prompt',
          text: `${relative.name} needs at least ${result.minEntriesRequired} recorded videos for Selfie Mode. Currently has ${result.entryCount}.`,
        }]);
      } else {
        setMessages([{
          id: 'welcome-prompt',
          role: 'prompt',
          text: `Chat with ${relative.name}! Ask them anything about their life, memories, or experiences.`,
        }]);
      }
    } catch (error) {
      console.error('Failed to check readiness:', error);
      setMessages([{
        id: 'welcome-prompt',
        role: 'prompt',
        text: `Chat with ${relative.name}`,
      }]);
    }
  };

  const sendMessage = async () => {
    const trimmed = inputText.trim();
    if (!trimmed || isThinking) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: trimmed,
    };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsThinking(true);

    try {
      // Build API messages from chat history (only user and responder roles)
      const apiMessages = [...messages, userMessage]
        .filter(m => m.role === 'user' || m.role === 'responder')
        .map(m => ({
          role: m.role === 'user' ? 'user' : 'assistant',
          content: m.text,
        }));

      const result = await ApiService.sendVirtualChatMessage(
        relative.uuid,
        conversationUuid,
        apiMessages
      );

      setConversationUuid(result.conversationUuid);

      // Count existing responder messages for messageIndex
      const responderCount = messages.filter(m => m.role === 'responder').length;

      const responderMessage = {
        id: `responder-${Date.now()}`,
        role: 'responder',
        text: result.response,
        messageIndex: responderCount,
        feedback: null,
        feedbackComment: '',
        showCommentField: false,
        feedbackSubmitted: false,
      };
      setMessages(prev => [...prev, responderMessage]);
    } catch (error) {
      if (error.message.includes('transcript') || error.message.includes('Not enough')) {
        setNeedsMoreVideos(true);
        setMessages(prev => [...prev, {
          id: `error-${Date.now()}`,
          role: 'prompt',
          text: 'More recorded videos are needed before Selfie Mode can work.',
        }]);
      } else {
        setMessages(prev => [...prev, {
          id: `error-${Date.now()}`,
          role: 'prompt',
          text: `Something went wrong. Please try again.`,
        }]);
      }
    }
    setIsThinking(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const submitFeedback = async (messageId, rating) => {
    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, feedback: rating, showCommentField: true } : m
    ));

    const message = messages.find(m => m.id === messageId);
    if (!message || !conversationUuid) return;

    try {
      await ApiService.submitChatFeedback(
        conversationUuid,
        message.messageIndex,
        rating === 'up' ? 'like' : 'dislike',
        ''
      );
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  const submitFeedbackComment = async (messageId) => {
    const message = messages.find(m => m.id === messageId);
    if (!message || !conversationUuid || !message.feedbackComment.trim()) return;

    try {
      await ApiService.submitChatFeedback(
        conversationUuid,
        message.messageIndex,
        message.feedback === 'up' ? 'like' : 'dislike',
        message.feedbackComment
      );
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, feedbackSubmitted: true, showCommentField: false } : m
      ));
    } catch (error) {
      console.error('Failed to submit feedback comment:', error);
    }
  };

  const updateFeedbackComment = (messageId, text) => {
    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, feedbackComment: text } : m
    ));
  };

  const getInitial = (name) => name ? name[0].toUpperCase() : '?';

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Close Button */}
      <button
        onClick={onBack}
        className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/15 rounded-full flex items-center justify-center hover:bg-white/25 transition-colors"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      {/* Header */}
      <div className="flex flex-col items-center pt-8 pb-4 px-4">
        <div className="relative">
          {profileImageUrl ? (
            <img
              src={profileImageUrl}
              alt={relative.name}
              className="w-24 h-24 rounded-full object-cover border border-white/20"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gray-700 flex items-center justify-center border border-white/20">
              <span className="text-3xl font-semibold text-gray-400">{getInitial(relative.name)}</span>
            </div>
          )}
          <div className="absolute -top-1 -right-1 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center border-2 border-black">
            <Bot size={16} className="text-white" />
          </div>
        </div>
        <h2 className="text-white text-xl font-bold mt-3" style={{ fontFamily: 'serif' }}>
          Selfie Mode!
        </h2>
        <p className="text-purple-400 text-xs mt-1 underline">
          Selfie mode improves as you record videos!
        </p>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
        {messages.map((msg) => {
          if (msg.role === 'prompt') {
            return (
              <div key={msg.id} className="flex justify-center py-2">
                <p className="text-purple-400 text-sm text-center px-8">{msg.text}</p>
              </div>
            );
          }

          if (msg.role === 'user') {
            return (
              <div key={msg.id} className="flex justify-end">
                <div className="bg-purple-600/85 text-white px-4 py-2.5 rounded-2xl max-w-[75%] ml-16">
                  <p className="text-sm">{msg.text}</p>
                </div>
              </div>
            );
          }

          if (msg.role === 'responder') {
            return (
              <div key={msg.id} className="space-y-1">
                <div className="flex items-end gap-2">
                  {/* Avatar */}
                  {profileImageUrl ? (
                    <img src={profileImageUrl} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-gray-400">{getInitial(relative.name)}</span>
                    </div>
                  )}
                  {/* Bubble */}
                  <div className="bg-gray-800 text-white px-4 py-2.5 rounded-2xl max-w-[70%]">
                    <p className="text-sm">{msg.text}</p>
                  </div>
                  {/* Feedback buttons */}
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button
                      onClick={() => submitFeedback(msg.id, 'up')}
                      className={`p-1 rounded-full transition-colors ${
                        msg.feedback === 'up' ? 'text-green-400' : 'text-white/30 hover:text-white/60'
                      }`}
                    >
                      <ThumbsUp size={16} className={msg.feedback === 'up' ? 'fill-green-400' : ''} />
                    </button>
                    <button
                      onClick={() => submitFeedback(msg.id, 'down')}
                      className={`p-1 rounded-full transition-colors ${
                        msg.feedback === 'down' ? 'text-red-400' : 'text-white/30 hover:text-white/60'
                      }`}
                    >
                      <ThumbsDown size={16} className={msg.feedback === 'down' ? 'fill-red-400' : ''} />
                    </button>
                  </div>
                </div>
                {/* Feedback comment field */}
                {msg.showCommentField && !msg.feedbackSubmitted && (
                  <div className="ml-10 flex items-center gap-2 mt-1">
                    <input
                      type="text"
                      value={msg.feedbackComment}
                      onChange={(e) => updateFeedbackComment(msg.id, e.target.value)}
                      placeholder="Add a note (optional)..."
                      className="flex-1 bg-gray-800 text-white text-xs px-3 py-2 rounded-2xl focus:outline-none placeholder-gray-500"
                    />
                    <button
                      onClick={() => submitFeedbackComment(msg.id)}
                      className={`p-1 rounded-full transition-colors ${
                        msg.feedbackComment.trim() ? 'text-green-400' : 'text-white/30'
                      }`}
                    >
                      <Check size={16} />
                    </button>
                  </div>
                )}
              </div>
            );
          }

          return null;
        })}

        {/* Thinking indicator */}
        {isThinking && (
          <div className="flex items-end gap-2">
            {profileImageUrl ? (
              <img src={profileImageUrl} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-semibold text-gray-400">{getInitial(relative.name)}</span>
              </div>
            )}
            <div className="bg-gray-800 px-4 py-3 rounded-2xl">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Bar */}
      {!needsMoreVideos && (
        <div className="p-3 bg-black border-t border-gray-800">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              className="flex-1 bg-gray-800 text-white text-sm px-4 py-3 rounded-2xl focus:outline-none placeholder-gray-500"
            />
            {inputText.trim() && (
              <button
                onClick={sendMessage}
                disabled={isThinking}
                className="text-purple-400 hover:text-purple-300 disabled:opacity-50 transition-colors"
              >
                <ArrowUpCircle size={32} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default VirtualChatView;
